import React, { useState, useEffect, useMemo } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { create, all } from 'mathjs';
import Resource from './Resource';
import './ResourcePool.css';

// Initialize mathjs
const math = create(all);
// Define a custom base unit 'u' for "units" or other dimensionless quantities
math.createUnit('u');

const ResourcePool = ({ resources, onAddResource, onUpdateResource, onDeleteResource, onSaveToFirebase, hasUnsavedChanges, onUpdateResourceParams, onUpdateResourceTags }) => {
  // --- Local Storage Keys ---
  const EXPANDED_RESOURCES_KEY = 'draggitRM_expandedResources';
  const ACTIVE_DETAIL_VIEW_KEY = 'draggitRM_activeDetailView';
  const VISIBLE_KEY = 'draggitRM_resourcePoolVisible';
  const GROUPING_KEY = 'draggitRM_groupingKey';
  const STACK_KEY = 'draggitRM_stackIdentical';

  // --- State Initialization with localStorage ---
  const [isAddingResource, setIsAddingResource] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [expandedResources, setExpandedResources] = useState(() => {
    const stored = localStorage.getItem(EXPANDED_RESOURCES_KEY);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  });
  
  const [activeDetailView, setActiveDetailView] = useState(() => {
    return localStorage.getItem(ACTIVE_DETAIL_VIEW_KEY) || 'info';
  });
  
  const [visible, setVisible] = useState(() => {
    const stored = localStorage.getItem(VISIBLE_KEY);
    return stored ? JSON.parse(stored) : true; // Default to visible
  });
  
  const [groupingKey, setGroupingKey] = useState(() => {
    return localStorage.getItem(GROUPING_KEY) || '';
  });
  
  const [stackIdentical, setStackIdentical] = useState(() => {
    const stored = localStorage.getItem(STACK_KEY);
    return stored ? JSON.parse(stored) : false;
  });
  
  const [formData, setFormData] = useState({
    name: '',
    role: 'Developer',
    skillLevel: 'Mid',
    isManager: false,
  });

  // --- Effects to Persist State ---
  useEffect(() => {
    localStorage.setItem(EXPANDED_RESOURCES_KEY, JSON.stringify(Array.from(expandedResources)));
  }, [expandedResources]);

  useEffect(() => {
    localStorage.setItem(ACTIVE_DETAIL_VIEW_KEY, activeDetailView);
  }, [activeDetailView]);

  useEffect(() => {
    localStorage.setItem(VISIBLE_KEY, JSON.stringify(visible));
  }, [visible]);

  useEffect(() => {
    localStorage.setItem(GROUPING_KEY, groupingKey);
  }, [groupingKey]);

  useEffect(() => {
    localStorage.setItem(STACK_KEY, JSON.stringify(stackIdentical));
  }, [stackIdentical]);

  // --- Memoized Computations ---
  // Get all unique parameter keys from resources
  const allParamKeys = useMemo(() => {
    const keys = new Set();
    resources.forEach(resource => {
      if (resource.params) {
        Object.keys(resource.params).forEach(key => {
          if (key !== 'order') keys.add(key);
        });
      }
    });
    return Array.from(keys).sort();
  }, [resources]);

  // --- Toggle Handlers ---
  const toggleExpanded = (resourceId) => {
    setExpandedResources(prev => {
      const newSet = new Set(prev);
      if (newSet.has(resourceId)) {
        newSet.delete(resourceId);
      } else {
        newSet.add(resourceId);
      }
      return newSet;
    });
  };

  const { setNodeRef, isOver } = useDroppable({
    id: 'resource-pool',
    data: { type: 'resource-pool' }
  });

  const handleAddResource = () => {
    if (formData.name.trim() && formData.role) {
      onAddResource(
        formData.name.trim(),
        formData.role,
        formData.skillLevel,
        formData.isManager
      );
      setFormData({ name: '', role: 'Developer', skillLevel: 'Mid', isManager: false });
      setIsAddingResource(false);
    }
  };

  const cancelAdd = () => {
    setIsAddingResource(false);
    setFormData({ name: '', role: 'Developer', skillLevel: 'Mid', isManager: false });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddResource();
    } else if (e.key === 'Escape') {
      cancelAdd();
    }
  };

  // Filter resources based on search query
  const filteredResources = useMemo(() => {
    let result = resources;
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(resource => 
        resource.name.toLowerCase().includes(query) ||
        resource.role.toLowerCase().includes(query) ||
        resource.skillLevel.toLowerCase().includes(query)
      );
    }
    
    return result;
  }, [resources, searchQuery]);

  // Generate stack key for a resource
  const getResourceStackKey = (resource) => {
    if (!stackIdentical) return null;
    const groupValue = groupingKey ? (resource.params?.[groupingKey] || 'ungrouped') : 'all';
    return `${resource.role}-${resource.skillLevel}-${groupValue}`;
  };

  // Stack identical resources
  const displayItems = useMemo(() => {
    if (!stackIdentical) {
      return filteredResources.map(r => ({
        id: r.id,
        resource: r,
        count: 1,
        resourceIds: [r.id],
      }));
    }

    const stacks = new Map();
    filteredResources.forEach(resource => {
      const stackKey = getResourceStackKey(resource);
      if (!stacks.has(stackKey)) {
        stacks.set(stackKey, {
          id: `stack-${stackKey}`,
          resource: resource, // Representative resource
          count: 0,
          resourceIds: [],
        });
      }
      const stack = stacks.get(stackKey);
      stack.count++;
      stack.resourceIds.push(resource.id);
    });

    return Array.from(stacks.values());
  }, [filteredResources, stackIdentical, groupingKey]);

  return (
    <div className={`resource-pool ${isOver ? 'over' : ''} ${!visible ? 'resource-pool-hidden' : ''}`}>
      <div className="resource-pool-header">
        {/* <h4>Available Resources</h4> */}
        {visible && (
          <>
            {/* Detail View Controls at Pool Level */}
            <select
              className="detail-view-controls"
              value={activeDetailView}
              onChange={(e) => setActiveDetailView(e.target.value)}
              title="Select detail view for all resources"
            >
              <option value="info">Info</option>
              <option value="params">Parameters</option>
              <option value="tags">Tags</option>
            </select>
            
            {/* Grouping Selector */}
            {allParamKeys.length > 0 && (
              <select
                className="detail-view-controls"
                value={groupingKey}
                onChange={(e) => setGroupingKey(e.target.value)}
                title="Group by Parameter"
              >
                <option value="">No Grouping</option>
                {allParamKeys.map(key => (
                  <option key={key} value={key}>Group: {key}</option>
                ))}
              </select>
            )}
            
            {/* Stack Identical Checkbox */}
            <input
              className="detail-view-assembly"
              type="checkbox"
              checked={stackIdentical}
              onChange={(e) => setStackIdentical(e.target.checked)}
              title="Stack identical resources"
            />
          </>
        )}
        
        {/* Toggle Visibility Button */}
        <button
          className="resource-pool-toggle-button"
          onClick={() => setVisible(!visible)}
          title={visible ? 'Hide Resource Pool' : 'Show Resource Pool'}
        >
          {visible ? '➖' : '➕'}
        </button>
      </div>

      {visible && (
        <>
          {/* Search/Filter Input */}
          <div className="resource-pool-search">
            <input
              type="text"
              placeholder="🔍 Search resources..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="resource-pool-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="resource-pool-search-clear"
                title="Clear search"
              >
                ×
              </button>
            )}
          </div>

          <div ref={setNodeRef} className="resource-pool-content">
            <SortableContext items={displayItems.map(item => item.id)} strategy={verticalListSortingStrategy}>
              {displayItems.length > 0 ? (
                displayItems.map((item) => (
                  <Resource
                    key={item.id}
                    id={item.resource.id}
                    name={item.resource.name}
                    role={item.resource.role}
                    skillLevel={item.resource.skillLevel}
                    isManager={item.resource.isManager}
                    params={item.resource.params || {}}
                    tags={item.resource.tags || []}
                    onEdit={onUpdateResource}
                    onDelete={onDeleteResource}
                    onUpdateParams={onUpdateResourceParams}
                    onUpdateTags={onUpdateResourceTags}
                    isInPool={true}
                    activeDetailView={activeDetailView}
                    count={item.count}
                    resourceIds={item.resourceIds}
                    isExpanded={expandedResources.has(item.resource.id)}
                    onToggleExpand={() => toggleExpanded(item.resource.id)}
                  />
                ))
              ) : (
                <div className="resource-pool-no-results">
                  {searchQuery ? 'No resources match your search' : 'No resources available'}
                </div>
              )}
            </SortableContext>
          </div>

          {isAddingResource && (
            <div className="resource-add-form">
              <input
                type="text"
                placeholder="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                onKeyDown={handleKeyPress}
                className="resource-add-input"
                autoFocus
              />
              <select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="resource-add-select"
              >
                <option value="Manager">Manager</option>
                <option value="Developer">Developer</option>
                <option value="Designer">Designer</option>
                <option value="Tester">Tester</option>
                <option value="Analyst">Analyst</option>
                <option value="Architect">Architect</option>
              </select>
              <select
                value={formData.skillLevel}
                onChange={(e) => setFormData({ ...formData, skillLevel: e.target.value })}
                className="resource-add-select"
              >
                <option value="Junior">Junior</option>
                <option value="Mid">Mid</option>
                <option value="Senior">Senior</option>
              </select>
              <label className="resource-add-checkbox">
                <input
                  type="checkbox"
                  checked={formData.isManager}
                  onChange={(e) => setFormData({ ...formData, isManager: e.target.checked })}
                />
                <span>Manager</span>
              </label>
              <div className="resource-add-actions">
                <button onClick={handleAddResource} className="resource-add-save">
                  Add
                </button>
                <button onClick={cancelAdd} className="resource-add-cancel">
                  Cancel
                </button>
              </div>
            </div>
          )}
          
          {!isAddingResource && (
            <div>
              <button
                onClick={() => setIsAddingResource(true)}
                className="resource-pool-add-button-header"
                title="Add new resource"
              >
                +
              </button>
              <button
                onClick={onSaveToFirebase}
                className={`resource-pool-save-button ${hasUnsavedChanges ? 'has-changes' : ''}`}
                disabled={!hasUnsavedChanges}
              >
                {hasUnsavedChanges ? '💾' : '✓ Pool Saved'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ResourcePool;
